import { useEffect, useRef, useState } from "react";
import { CheckCircle2, LoaderCircle, Mic, RotateCcw, Square, TriangleAlert, X } from "lucide-react";
import { api, type MeetingSession } from "../api.js";
import type { OpsMediaOwnerController } from "./ops-media-owner.js";

type Notify = (text: string, tone?: "success" | "error") => void;
type RecordingState = "IDLE" | "STARTING" | "RECORDING" | "STOPPING";

const SEGMENT_MILLISECONDS = 60_000;
const STATUS_POLL_MILLISECONDS = 2_000;

export function MeetingCapture({ notify, afterReady, mediaOwner }: {
  notify: Notify;
  afterReady: () => Promise<void>;
  mediaOwner: OpsMediaOwnerController;
}) {
  const [meeting, setMeeting] = useState<MeetingSession | null>(null);
  const [recordingState, setRecordingState] = useState<RecordingState>("IDLE");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState("");
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const activeRef = useRef(false);
  const rotationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stopResolveRef = useRef<(() => void) | null>(null);
  const uploadChainRef = useRef<Promise<void>>(Promise.resolve());
  const uploadErrorRef = useRef<unknown>(null);
  const ordinalRef = useRef(0);
  const readyNotifiedRef = useRef<string | null>(null);
  const trackWatcherRef = useRef<(() => void) | null>(null);

  const releaseOwnedStream = (stream = streamRef.current): boolean => {
    trackWatcherRef.current?.();
    trackWatcherRef.current = null;
    if (!stream) return mediaOwner.release("meeting");
    const stopped = stopMeetingTracksAndRelease(stream, mediaOwner);
    if (streamRef.current === stream) streamRef.current = null;
    return stopped;
  };

  useEffect(() => {
    let cancelled = false;
    void api.latestMeeting().then(({ meeting: latest }) => {
      if (!cancelled) setMeeting(latest);
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (recordingState !== "RECORDING") return;
    const started = Date.now() - elapsedSeconds * 1_000;
    const timer = window.setInterval(() => setElapsedSeconds(Math.floor((Date.now() - started) / 1_000)), 1_000);
    return () => window.clearInterval(timer);
  }, [recordingState]);

  useEffect(() => {
    if (!meeting || !["STOPPED", "PROCESSING"].includes(meeting.state)) return;
    let cancelled = false;
    const poll = window.setInterval(() => {
      void api.meeting(meeting.id).then(async (current) => {
        if (cancelled) return;
        setMeeting(current);
        if (current.state === "READY_FOR_REVIEW" && readyNotifiedRef.current !== current.id) {
          readyNotifiedRef.current = current.id;
          await afterReady();
          notify(current.proposalIds.length
            ? `${current.proposalIds.length} meeting ${current.proposalIds.length === 1 ? "action is" : "actions are"} ready to review.`
            : "Meeting summary is ready. No action was added without your approval.");
        }
      }).catch(() => undefined);
    }, STATUS_POLL_MILLISECONDS);
    return () => { cancelled = true; window.clearInterval(poll); };
  }, [afterReady, meeting, notify]);

  useEffect(() => () => {
    activeRef.current = false;
    if (rotationTimerRef.current) clearTimeout(rotationTimerRef.current);
    const recorder = recorderRef.current;
    if (recorder?.state === "recording") recorder.stop();
    releaseOwnedStream();
  }, [mediaOwner]);

  const start = async () => {
    if (recordingState !== "IDLE") return;
    setError("");
    setRecordingState("STARTING");
    let stream: MediaStream | null = null;
    let created: MeetingSession | null = null;
    try {
      const mimeType = selectMeetingMimeType();
      if (!mimeType || !navigator.mediaDevices?.getUserMedia) {
        throw new Error("This browser cannot record meeting audio.");
      }
      if (!await mediaOwner.claimForMeeting()) {
        throw new Error("Voice is still using the microphone. Stop Voice and try the meeting again.");
      }
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });
      streamRef.current = stream;
      trackWatcherRef.current = watchMeetingTrackEnd(stream, () => {
        if (streamRef.current !== stream) return;
        trackWatcherRef.current?.();
        trackWatcherRef.current = null;
        streamRef.current = null;
        mediaOwner.release("meeting");
        if (!activeRef.current) return;
        activeRef.current = false;
        if (rotationTimerRef.current) clearTimeout(rotationTimerRef.current);
        rotationTimerRef.current = null;
        const recorder = recorderRef.current;
        if (recorder?.state === "recording") recorder.stop();
        setError("The microphone stopped. Saved meeting audio remains available to prepare or discard.");
        setRecordingState("IDLE");
      });
      created = await api.startMeeting(undefined, crypto.randomUUID());
      if (mediaOwner.owner !== "meeting" || stream.getTracks().every((track) => track.readyState === "ended")) {
        throw new Error("The microphone ended before meeting recording could start.");
      }
      ordinalRef.current = 0;
      uploadErrorRef.current = null;
      uploadChainRef.current = Promise.resolve();
      activeRef.current = true;
      setMeeting(created);
      setElapsedSeconds(0);
      setRecordingState("RECORDING");
      startRecorderCycle({
        stream,
        mimeType,
        meetingId: created.id,
        activeRef,
        recorderRef,
        rotationTimerRef,
        stopResolveRef,
        uploadChainRef,
        uploadErrorRef,
        ordinalRef,
        fail: (cause) => {
          activeRef.current = false;
          const recorder = recorderRef.current;
          if (recorder?.state === "recording") recorder.stop();
          setError(cause instanceof Error ? cause.message : "Meeting audio could not upload.");
          setRecordingState("IDLE");
          releaseOwnedStream();
        }
      });
    } catch (cause) {
      activeRef.current = false;
      if (stream) releaseOwnedStream(stream);
      else mediaOwner.release("meeting");
      if (created) void api.cancelMeeting(created.id).catch(() => undefined);
      setError(cause instanceof Error ? cause.message : "Meeting recording could not start.");
      setRecordingState("IDLE");
    }
  };

  const stop = async () => {
    if (!meeting || recordingState !== "RECORDING") return;
    setRecordingState("STOPPING");
    activeRef.current = false;
    if (rotationTimerRef.current) clearTimeout(rotationTimerRef.current);
    rotationTimerRef.current = null;
    const recorder = recorderRef.current;
    if (recorder?.state === "recording") {
      await new Promise<void>((resolve) => {
        stopResolveRef.current = resolve;
        recorder.stop();
      });
    }
    releaseOwnedStream();
    recorderRef.current = null;
    try {
      await uploadChainRef.current;
      if (uploadErrorRef.current) throw uploadErrorRef.current;
      const stopped = await api.stopMeeting(meeting.id, `meeting-stop-${meeting.id}`);
      setMeeting(stopped);
      setRecordingState("IDLE");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Meeting audio could not be prepared.");
      setRecordingState("IDLE");
    }
  };

  const recover = async () => {
    if (!meeting) return;
    setError("");
    try {
      setMeeting(await api.stopMeeting(meeting.id, `meeting-recover-${meeting.id}`));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Meeting analysis could not restart.");
    }
  };

  const discard = async () => {
    if (!meeting) return;
    try {
      await api.cancelMeeting(meeting.id, `meeting-cancel-${meeting.id}`);
      setMeeting(null);
      setError("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Meeting could not be discarded.");
    }
  };

  const localRecording = recordingState === "RECORDING" || recordingState === "STOPPING";
  const interrupted = meeting?.state === "RECORDING" && !localRecording;
  const processing = meeting && ["STOPPED", "PROCESSING"].includes(meeting.state);
  const ready = meeting?.state === "READY_FOR_REVIEW";
  const failed = meeting?.state === "FAILED";

  return (
    <section className={`meeting-capture ${localRecording ? "meeting-capture--recording" : ""}`} aria-label="Meeting capture">
      {!meeting || (!localRecording && !processing && !ready && !failed && !interrupted) ? (
        <button className="meeting-start" type="button" onClick={() => void start()} disabled={recordingState === "STARTING"}>
          <span className="meeting-start__icon">{recordingState === "STARTING" ? <LoaderCircle className="spin" size={21} /> : <Mic size={21} />}</span>
          <span><strong>{recordingState === "STARTING" ? "Opening microphone…" : "Start meeting"}</strong><small>Record, summarize, and prepare tasks for approval</small></span>
        </button>
      ) : null}

      {localRecording ? <div className="meeting-live" role="status">
        <span className="meeting-live__pulse" aria-hidden="true" />
        <span><strong>Recording meeting</strong><small>{formatElapsed(elapsedSeconds)} · Audio is saved in safe segments</small></span>
        <button type="button" onClick={() => void stop()} disabled={recordingState === "STOPPING"}>
          {recordingState === "STOPPING" ? <LoaderCircle className="spin" size={17} /> : <Square size={15} fill="currentColor" />}
          {recordingState === "STOPPING" ? "Saving…" : "Stop & prepare"}
        </button>
      </div> : null}

      {processing ? <div className="meeting-result meeting-result--processing" role="status">
        <LoaderCircle className="spin" size={19} aria-hidden="true" />
        <span><strong>Preparing the meeting</strong><small>{meeting.readySegmentCount}/{meeting.segmentCount} audio segments transcribed · tasks still require approval</small></span>
      </div> : null}

      {interrupted ? <div className="meeting-result meeting-result--warning" role="alert">
        <TriangleAlert size={19} aria-hidden="true" />
        <span><strong>Recording was interrupted</strong><small>{meeting.segmentCount ? "Use the saved audio to prepare tasks, or discard it." : "No audio segment reached the server."}</small></span>
        <div>{meeting.segmentCount ? <button type="button" onClick={() => void recover()}><RotateCcw size={15} />Prepare saved audio</button> : null}<button type="button" onClick={() => void discard()}><X size={15} />Discard</button></div>
      </div> : null}

      {failed ? <div className="meeting-result meeting-result--warning" role="alert">
        <TriangleAlert size={19} aria-hidden="true" />
        <span><strong>Meeting needs another try</strong><small>The saved audio was not applied to the board. Retry the safe analysis.</small></span>
        <div><button type="button" onClick={() => void recover()}><RotateCcw size={15} />Try again</button><button type="button" onClick={() => void discard()}><X size={15} />Discard</button></div>
      </div> : null}

      {ready ? <details className="meeting-result meeting-result--ready">
        <summary><CheckCircle2 size={19} aria-hidden="true" /><span><strong>Meeting ready</strong><small>{meeting.proposalIds.length ? `${meeting.proposalIds.length} ${meeting.proposalIds.length === 1 ? "action" : "actions"} prepared below` : "No action was added"}</small></span></summary>
        <p>{meeting.summary}</p>
        {meeting.clarifications.length ? <ul>{meeting.clarifications.map((item) => <li key={item}>{item}</li>)}</ul> : null}
        <button type="button" className="meeting-new" onClick={() => setMeeting(null)}><Mic size={15} />Start another meeting</button>
      </details> : null}

      {error ? <p className="meeting-error" role="alert">{error}</p> : null}
    </section>
  );
}

export function stopMeetingTracksAndRelease(
  stream: Pick<MediaStream, "getTracks">,
  mediaOwner: OpsMediaOwnerController
): boolean {
  const tracks = stream.getTracks();
  for (const track of tracks) {
    try { track.stop(); } catch { /* retain the lease unless every track proves ended */ }
  }
  const allEnded = tracks.every((track) => track.readyState === "ended");
  // WHY [BOL-MEETING-MIC-RELEASE-001]: stopping MediaRecorder does not release
  // its source. Stop every physical track, then release the shared owner only
  // after each track reports ended; otherwise Voice must stay locked instead
  // of racing a microphone whose teardown is unknown. Falsifier: Voice can
  // listen while any meeting track is live, or a stopped meeting strands the
  // lease after all tracks are ended.
  if (allEnded) mediaOwner.release("meeting");
  return allEnded;
}

function watchMeetingTrackEnd(stream: Pick<MediaStream, "getTracks">, allEnded: () => void): () => void {
  const tracks = stream.getTracks();
  const observe = () => {
    if (tracks.every((track) => track.readyState === "ended")) allEnded();
  };
  for (const track of tracks) track.addEventListener("ended", observe);
  return () => {
    for (const track of tracks) track.removeEventListener("ended", observe);
  };
}

export function selectMeetingMimeType(
  recorder: Pick<typeof MediaRecorder, "isTypeSupported"> = MediaRecorder
): string | null {
  return ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/mp4"]
    .find((mimeType) => recorder.isTypeSupported(mimeType)) ?? null;
}

function startRecorderCycle(input: {
  stream: MediaStream;
  mimeType: string;
  meetingId: string;
  activeRef: { current: boolean };
  recorderRef: { current: MediaRecorder | null };
  rotationTimerRef: { current: ReturnType<typeof setTimeout> | null };
  stopResolveRef: { current: (() => void) | null };
  uploadChainRef: { current: Promise<void> };
  uploadErrorRef: { current: unknown };
  ordinalRef: { current: number };
  fail: (error: unknown) => void;
}): void {
  if (!input.activeRef.current) return;
  const recorder = new MediaRecorder(input.stream, {
    mimeType: input.mimeType,
    audioBitsPerSecond: 32_000
  });
  input.recorderRef.current = recorder;
  recorder.ondataavailable = (event) => {
    if (!event.data.size || input.uploadErrorRef.current) return;
    const ordinal = input.ordinalRef.current++;
    input.uploadChainRef.current = input.uploadChainRef.current
      .then(async () => {
        await api.uploadMeetingSegment(
          input.meetingId,
          ordinal,
          event.data,
          `meeting-${input.meetingId}-segment-${ordinal}`
        );
      })
      .catch((error) => {
        input.uploadErrorRef.current = error;
        input.fail(error);
      });
  };
  recorder.onstop = () => {
    if (input.rotationTimerRef.current) clearTimeout(input.rotationTimerRef.current);
    input.rotationTimerRef.current = null;
    if (input.activeRef.current && !input.uploadErrorRef.current) startRecorderCycle(input);
    else {
      input.stopResolveRef.current?.();
      input.stopResolveRef.current = null;
    }
  };
  recorder.start();
  input.rotationTimerRef.current = setTimeout(() => {
    if (recorder.state === "recording") recorder.stop();
  }, SEGMENT_MILLISECONDS);
}

function formatElapsed(seconds: number): string {
  const minutes = Math.floor(seconds / 60).toString().padStart(2, "0");
  return `${minutes}:${(seconds % 60).toString().padStart(2, "0")}`;
}
